import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAdminContext } from '@/hooks/useAdminContext';
import { supabase } from '@/integrations/supabase/client';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowDownRight,
  ArrowUpDown,
  ArrowUpRight,
  BarChart3,
  Building2,
  FileText,
  Minus,
  Sparkles,
  Tags,
  User,
  Users,
  Workflow,
} from 'lucide-react';
import { Area, Bar, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { TopPromptsRanked } from '@/components/exec/TopPromptsRanked';
import { DeltaPeriodSelector, type DeltaPeriod } from '@/components/exec/DeltaPeriodSelector';
import { ModelSelectionCard } from '@/components/exec/ModelSelectionCard';
import { startOfDay, startOfWeek, addDays, toDateKey, toWeekKey, average } from '@/lib/dateUtils';
import { ScoreDisplay } from '@/lib/scoreUtils';

interface PromptTypeData {
  org_id: string;
  prompt_type: string;
  count: number;
}

interface TagDistributionData {
  org_id: string;
  tag: string;
  count: number;
}

interface WorkflowMetrics {
  total_created: number;
  total_released: number;
  created_in_period: number;
  released_in_period: number;
  completion_rate: number | null;
}

interface OrgMomentumStats {
  total_prompts: number;
  daily_prompt_streak: number;
  weekly_prompt_streak: number;
  prompts_this_week: number;
}

interface OrgJourneyPoint {
  point_order: number;
  label: string;
  period_start: string;
  score: number | null;
  activity: number;
  point_type: 'raw' | 'cosmo' | 'trend';
}

interface DepartmentTrend {
  department: string;
  avg_prompt_score: number | null;
  score_delta: number | null;
  prompt_count: number;
  active_users: number;
  total_prompt_count?: number;
}

interface UserPromptRollup {
  org_id: string;
  user_id: string;
  full_name: string;
  department: string;
  avg_prompt_score: number | null;
  prompt_count: number;
  daily_streak: number;
  weekly_streak: number;
  momentum: 'rising' | 'falling' | 'stable';
}

type UserSort = 'score_desc' | 'score_asc' | 'prompts_desc' | 'daily_streak_desc' | 'weekly_streak_desc';

interface RawScoredItem {
  user_id: string | null;
  created_at: string | null;
  score_total_1_100: number | null;
  original_score_1_100: number | null;
}

interface OrgMember {
  user_id: string;
  department: string | null;
}

interface ProfileRow {
  user_id: string;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
}

interface LegacyKpiData {
  day: string;
  total_prompts: number;
  active_users: number;
}

interface LegacyUserRollup {
  org_id: string;
  user_id: string;
  full_name: string | null;
  department: string | null;
  prompts: number;
  fsr: number | null;
  momentum: string | null;
}

interface LegacyTopPrompt {
  score_total: number | null;
  created_at: string | null;
}

function getDaysFromPeriod(period: DeltaPeriod): number {
  switch (period) {
    case 'weekly': return 7;
    case 'monthly': return 30;
    case 'quarterly': return 90;
    case 'yearly': return 365;
  }
}

function formatDelta(delta: number | null) {
  if (delta === null || Number.isNaN(Number(delta))) return 'Flat';
  if (delta > 0) return `+${Number(delta).toFixed(1)}`;
  return Number(delta).toFixed(1);
}

function getMomentumIcon(momentum: string) {
  switch (momentum) {
    case 'rising': return <ArrowUpRight className="h-4 w-4 text-green-500" />;
    case 'falling': return <ArrowDownRight className="h-4 w-4 text-red-500" />;
    default: return <Minus className="h-4 w-4 text-muted-foreground" />;
  }
}

function getDailyStreak(items: RawScoredItem[], userId?: string) {
  const dayKeys = new Set(
    items
      .filter((item) => item.created_at && (!userId || item.user_id === userId))
      .map((item) => toDateKey(new Date(item.created_at as string)))
  );

  let streak = 0;
  let cursor = startOfDay(new Date());
  while (dayKeys.has(toDateKey(cursor))) {
    streak += 1;
    cursor = addDays(cursor, -1);
  }
  return streak;
}

function getWeeklyStreak(items: RawScoredItem[], userId?: string) {
  const weekCounts = new Map<string, number>();
  items
    .filter((item) => item.created_at && (!userId || item.user_id === userId))
    .forEach((item) => {
      const weekKey = toWeekKey(new Date(item.created_at as string));
      weekCounts.set(weekKey, (weekCounts.get(weekKey) || 0) + 1);
    });

  let cursor = startOfWeek(new Date());
  if ((weekCounts.get(toWeekKey(cursor)) || 0) < 3) {
    cursor = addDays(cursor, -7);
  }

  let streak = 0;
  while ((weekCounts.get(toWeekKey(cursor)) || 0) >= 3) {
    streak += 1;
    cursor = addDays(cursor, -7);
  }
  return streak;
}

function getDailyStreakFromKpis(kpis: LegacyKpiData[]) {
  const dayKeys = new Set(
    kpis
      .filter((item) => Number(item.total_prompts) > 0)
      .map((item) => toDateKey(new Date(item.day)))
  );

  let streak = 0;
  let cursor = startOfDay(new Date());
  while (dayKeys.has(toDateKey(cursor))) {
    streak += 1;
    cursor = addDays(cursor, -1);
  }
  return streak;
}

function getWeeklyStreakFromKpis(kpis: LegacyKpiData[]) {
  const weekCounts = new Map<string, number>();
  kpis.forEach((item) => {
    const weekKey = toWeekKey(new Date(item.day));
    weekCounts.set(weekKey, (weekCounts.get(weekKey) || 0) + Number(item.total_prompts || 0));
  });

  let cursor = startOfWeek(new Date());
  if ((weekCounts.get(toWeekKey(cursor)) || 0) < 3) {
    cursor = addDays(cursor, -7);
  }

  let streak = 0;
  while ((weekCounts.get(toWeekKey(cursor)) || 0) >= 3) {
    streak += 1;
    cursor = addDays(cursor, -7);
  }
  return streak;
}

function buildJourneyFromScores(items: RawScoredItem[], days: number): OrgJourneyPoint[] {
  const validItems = items
    .filter((item): item is RawScoredItem & { created_at: string; score_total_1_100: number } =>
      Boolean(item.created_at) && typeof item.score_total_1_100 === 'number'
    )
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  const points: OrgJourneyPoint[] = [];
  const firstPairIndex = validItems.findIndex((item) =>
    typeof item.original_score_1_100 === 'number' &&
    item.original_score_1_100 <= item.score_total_1_100
  );

  if (firstPairIndex >= 0) {
    const firstPair = validItems[firstPairIndex];
    points.push({
      point_order: 0,
      label: 'First prompt',
      period_start: firstPair.created_at,
      score: firstPair.original_score_1_100,
      activity: 1,
      point_type: 'raw',
    });
    points.push({
      point_order: 1,
      label: 'With Cosmo',
      period_start: firstPair.created_at,
      score: firstPair.score_total_1_100,
      activity: 1,
      point_type: 'cosmo',
    });
  }

  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const firstPairDate = firstPairIndex >= 0 ? new Date(validItems[firstPairIndex].created_at) : null;
  const weekly = new Map<string, { scores: number[]; count: number; date: Date }>();

  validItems
    .filter((item) => {
      const createdAt = new Date(item.created_at);
      return createdAt >= cutoff && (!firstPairDate || createdAt > firstPairDate);
    })
    .forEach((item) => {
      const week = startOfWeek(new Date(item.created_at));
      const key = toDateKey(week);
      const entry = weekly.get(key) || { scores: [], count: 0, date: week };
      entry.scores.push(item.score_total_1_100);
      entry.count += 1;
      weekly.set(key, entry);
    });

  Array.from(weekly.values())
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .forEach((entry, index) => {
      points.push({
        point_order: points.length + index,
        label: entry.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        period_start: entry.date.toISOString(),
        score: average(entry.scores),
        activity: entry.count,
        point_type: 'trend',
      });
    });

  return points;
}

function buildLegacyJourneyFromPrompts(prompts: LegacyTopPrompt[], kpis: LegacyKpiData[]): OrgJourneyPoint[] {
  const promptPoints = prompts
    .filter((prompt): prompt is LegacyTopPrompt & { created_at: string; score_total: number } =>
      Boolean(prompt.created_at) && typeof prompt.score_total === 'number'
    )
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    .slice(-8);

  if (promptPoints.length > 0) {
    return promptPoints.map((prompt, index) => ({
      point_order: index,
      label: index === 0 ? 'Earlier' : new Date(prompt.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      period_start: prompt.created_at,
      score: prompt.score_total,
      activity: 1,
      point_type: 'trend',
    }));
  }

  return kpis
    .filter((item) => Number(item.total_prompts) > 0)
    .sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime())
    .slice(-8)
    .map((item, index) => ({
      point_order: index,
      label: new Date(item.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      period_start: item.day,
      score: null,
      activity: Number(item.total_prompts || 0),
      point_type: 'trend',
    }));
}

function buildFallbackDepartmentTrends(items: RawScoredItem[], members: OrgMember[], days: number): DepartmentTrend[] {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const previousCutoff = new Date();
  previousCutoff.setDate(previousCutoff.getDate() - days * 2);
  const memberDept = new Map(members.map((member) => [member.user_id, member.department || 'Unknown']));
  const departments = Array.from(new Set(members.map((member) => member.department || 'Unknown')));

  return departments.map((department) => {
    const departmentUserIds = new Set(members.filter((member) => (member.department || 'Unknown') === department).map((member) => member.user_id));
    const current = items.filter((item) =>
      item.user_id &&
      departmentUserIds.has(item.user_id) &&
      item.created_at &&
      new Date(item.created_at) >= cutoff &&
      typeof item.score_total_1_100 === 'number'
    );
    const previous = items.filter((item) =>
      item.user_id &&
      memberDept.get(item.user_id) === department &&
      item.created_at &&
      new Date(item.created_at) >= previousCutoff &&
      new Date(item.created_at) < cutoff &&
      typeof item.score_total_1_100 === 'number'
    );
    const currentAvg = average(current.map((item) => item.score_total_1_100 as number));
    const previousAvg = average(previous.map((item) => item.score_total_1_100 as number));

    return {
      department,
      avg_prompt_score: currentAvg !== null ? Number(currentAvg.toFixed(1)) : null,
      score_delta: currentAvg !== null && previousAvg !== null ? Number((currentAvg - previousAvg).toFixed(1)) : null,
      prompt_count: current.length,
      active_users: new Set(current.map((item) => item.user_id)).size,
    };
  }).sort((a, b) => (b.avg_prompt_score ?? -1) - (a.avg_prompt_score ?? -1) || b.prompt_count - a.prompt_count);
}

function buildFallbackUserRollup(
  items: RawScoredItem[],
  members: OrgMember[],
  profiles: ProfileRow[],
  days: number,
  departmentFilter: string
): UserPromptRollup[] {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const previousCutoff = new Date();
  previousCutoff.setDate(previousCutoff.getDate() - days * 2);
  const profileMap = new Map(profiles.map((profile) => [profile.user_id, profile]));

  return members
    .filter((member) => departmentFilter === 'All' || (member.department || 'Unknown') === departmentFilter)
    .map((member) => {
      const current = items.filter((item) =>
        item.user_id === member.user_id &&
        item.created_at &&
        new Date(item.created_at) >= cutoff &&
        typeof item.score_total_1_100 === 'number'
      );
      const previous = items.filter((item) =>
        item.user_id === member.user_id &&
        item.created_at &&
        new Date(item.created_at) >= previousCutoff &&
        new Date(item.created_at) < cutoff &&
        typeof item.score_total_1_100 === 'number'
      );
      const currentAvg = average(current.map((item) => item.score_total_1_100 as number));
      const previousAvg = average(previous.map((item) => item.score_total_1_100 as number));
      const profile = profileMap.get(member.user_id);

      return {
        org_id: '',
        user_id: member.user_id,
        full_name: profile?.full_name || `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'Unknown',
        department: member.department || 'Unknown',
        avg_prompt_score: currentAvg !== null ? Number(currentAvg.toFixed(1)) : null,
        prompt_count: current.length,
        daily_streak: getDailyStreak(items, member.user_id),
        weekly_streak: getWeeklyStreak(items, member.user_id),
        momentum: currentAvg !== null && previousAvg !== null && currentAvg > previousAvg
          ? 'rising'
          : currentAvg !== null && previousAvg !== null && currentAvg < previousAvg
            ? 'falling'
            : 'stable',
      };
    });
}

function buildLegacyDepartmentTrends(users: LegacyUserRollup[]): DepartmentTrend[] {
  const byDepartment = new Map<string, LegacyUserRollup[]>();
  users.forEach((user) => {
    const department = user.department || 'Unknown';
    byDepartment.set(department, [...(byDepartment.get(department) || []), user]);
  });

  return Array.from(byDepartment.entries())
    .map(([department, rows]) => {
      const scores = rows
        .map((row) => row.fsr)
        .filter((score): score is number => typeof score === 'number' && score > 0);
      return {
        department,
        avg_prompt_score: average(scores),
        score_delta: null,
        prompt_count: rows.reduce((sum, row) => sum + Number(row.prompts || 0), 0),
        active_users: rows.filter((row) => Number(row.prompts || 0) > 0).length,
      };
    })
    .sort((a, b) => (b.avg_prompt_score ?? -1) - (a.avg_prompt_score ?? -1) || b.prompt_count - a.prompt_count);
}

function buildLegacyUserRollup(users: LegacyUserRollup[]): UserPromptRollup[] {
  return users.map((user) => ({
    org_id: user.org_id,
    user_id: user.user_id,
    full_name: user.full_name || 'Unknown',
    department: user.department || 'Unknown',
    avg_prompt_score: typeof user.fsr === 'number' && user.fsr > 0 ? Number(user.fsr) : null,
    prompt_count: Number(user.prompts || 0),
    daily_streak: 0,
    weekly_streak: 0,
    momentum: user.momentum === 'rising' || user.momentum === 'falling' ? user.momentum : 'stable',
  }));
}

export default function ExecDashboard() {
  const { membership } = useAuth();
  const { isImpersonating, impersonatedOrgId, impersonatedOrgName } = useAdminContext();
  const navigate = useNavigate();

  const effectiveOrgId = isImpersonating ? impersonatedOrgId : membership?.org_id;
  const effectiveOrgName = isImpersonating ? impersonatedOrgName : membership?.org_name;

  const [loading, setLoading] = useState(true);
  const [deltaPeriod, setDeltaPeriod] = useState<DeltaPeriod>('monthly');
  const [departmentFilter, setDepartmentFilter] = useState('All');
  const [userSort, setUserSort] = useState<UserSort>('score_desc');
  const [journeyData, setJourneyData] = useState<OrgJourneyPoint[]>([]);
  const [departmentTrends, setDepartmentTrends] = useState<DepartmentTrend[]>([]);
  const [userRollup, setUserRollup] = useState<UserPromptRollup[]>([]);
  const [promptTypes, setPromptTypes] = useState<PromptTypeData[]>([]);
  const [tagDistribution, setTagDistribution] = useState<TagDistributionData[]>([]);
  const [workflowMetrics, setWorkflowMetrics] = useState<WorkflowMetrics | null>(null);
  const [orgMomentum, setOrgMomentum] = useState<OrgMomentumStats | null>(null);

  const dateRange = getDaysFromPeriod(deltaPeriod);

  useEffect(() => {
    if (effectiveOrgId) {
      fetchDashboardData();
    }
    // fetchDashboardData is intentionally scoped inside this component because it depends on fallback helpers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOrgId, dateRange, departmentFilter]);

  const fetchFallbackDashboardData = async () => {
    if (!effectiveOrgId) return false;

    const [scoresRes, membersRes] = await Promise.all([
      supabase
        .from('v_scored_items')
        .select('user_id, created_at, score_total_1_100, original_score_1_100')
        .eq('org_id', effectiveOrgId)
        .order('created_at', { ascending: true })
        .limit(10000),
      supabase
        .from('org_memberships')
        .select('user_id, department')
        .eq('org_id', effectiveOrgId)
        .eq('is_active', true),
    ]);

    if (scoresRes.error) console.error('Fallback scored items failed:', scoresRes.error);
    if (membersRes.error) console.error('Fallback org members failed:', membersRes.error);
    if (scoresRes.error || membersRes.error) return false;

    const scores = ((scoresRes.data || []) as RawScoredItem[]).filter((item) => item.created_at);
    const memberMap = new Map<string, OrgMember>(
      ((membersRes.data || []) as OrgMember[]).map((member) => [member.user_id, member])
    );

    scores.forEach((score) => {
      if (score.user_id && !memberMap.has(score.user_id)) {
        memberMap.set(score.user_id, { user_id: score.user_id, department: 'Unknown' });
      }
    });

    const members = Array.from(memberMap.values());
    const userIds = members.map((member) => member.user_id);
    let profiles: ProfileRow[] = [];

    if (userIds.length > 0) {
      const profilesRes = await supabase
        .from('profiles')
        .select('user_id, full_name, first_name, last_name')
        .in('user_id', userIds);

      if (profilesRes.error) {
        console.error('Fallback profiles failed:', profilesRes.error);
      } else {
        profiles = (profilesRes.data || []) as ProfileRow[];
      }
    }

    const thisWeekStart = startOfWeek(new Date());
    setJourneyData(buildJourneyFromScores(scores, dateRange));
    setDepartmentTrends(buildFallbackDepartmentTrends(scores, members, dateRange));
    setUserRollup(buildFallbackUserRollup(scores, members, profiles, dateRange, departmentFilter));
    setOrgMomentum({
      total_prompts: scores.length,
      daily_prompt_streak: getDailyStreak(scores),
      weekly_prompt_streak: getWeeklyStreak(scores),
      prompts_this_week: scores.filter((item) =>
        item.created_at && new Date(item.created_at) >= thisWeekStart
      ).length,
    });

    return scores.length > 0 || members.length > 0;
  };

  const fetchLegacyDashboardData = async () => {
    if (!effectiveOrgId) return false;

    const [kpiRes, usersRes, topPromptsRes] = await Promise.all([
      supabase.rpc('get_org_kpis_by_day', { _org_id: effectiveOrgId }),
      supabase.rpc('get_org_user_rollup', {
        _org_id: effectiveOrgId,
        _days: dateRange,
        _department: departmentFilter,
      }),
      supabase.rpc('get_org_top_prompts', { _org_id: effectiveOrgId, _limit: 50 }),
    ]);

    if (kpiRes.error) console.error('Legacy KPI fallback failed:', kpiRes.error);
    if (usersRes.error) console.error('Legacy user fallback failed:', usersRes.error);
    if (topPromptsRes.error) console.error('Legacy top prompts fallback failed:', topPromptsRes.error);
    if (kpiRes.error && usersRes.error && topPromptsRes.error) return false;

    const kpis = (kpiRes.data || []) as LegacyKpiData[];
    const users = (usersRes.data || []) as LegacyUserRollup[];
    const topPrompts = (topPromptsRes.data || []) as LegacyTopPrompt[];
    const periodCutoff = new Date();
    periodCutoff.setDate(periodCutoff.getDate() - dateRange);

    setJourneyData(buildLegacyJourneyFromPrompts(topPrompts, kpis));
    setDepartmentTrends(buildLegacyDepartmentTrends(users));
    setUserRollup(buildLegacyUserRollup(users));
    setPromptTypes([]);
    setTagDistribution([]);
    setWorkflowMetrics(null);
    setOrgMomentum({
      total_prompts: kpis.reduce((sum, item) => sum + Number(item.total_prompts || 0), 0),
      daily_prompt_streak: getDailyStreakFromKpis(kpis),
      weekly_prompt_streak: getWeeklyStreakFromKpis(kpis),
      prompts_this_week: kpis
        .filter((item) => new Date(item.day) >= startOfWeek(new Date()))
        .reduce((sum, item) => sum + Number(item.total_prompts || 0), 0),
    });

    const hasPromptData = topPrompts.length > 0 || kpis.some((item) => Number(item.total_prompts) > 0);
    const hasUserData = users.length > 0 || kpis.some((item) => new Date(item.day) >= periodCutoff);
    return hasPromptData || hasUserData;
  };

  const fetchDashboardData = async () => {
    if (!effectiveOrgId) return;
    setLoading(true);

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rpc = (supabase.rpc as any).bind(supabase);
      const [journeyRes, departmentsRes, usersRes, typesRes, tagsRes, momentumRes, workflowsRes] = await Promise.all([
        rpc('get_org_prompt_journey', { _org_id: effectiveOrgId, _days: dateRange }),
        rpc('get_org_department_prompt_trends', { _org_id: effectiveOrgId, _days: dateRange }),
        rpc('get_org_user_prompt_rollup_v2', {
          _org_id: effectiveOrgId,
          _days: dateRange,
          _department: departmentFilter,
        }),
        rpc('get_org_prompt_type_distribution', { _org_id: effectiveOrgId }),
        rpc('get_org_tag_distribution', { _org_id: effectiveOrgId }),
        rpc('get_org_momentum_stats', { _org_id: effectiveOrgId }),
        rpc('get_org_workflow_metrics', { _org_id: effectiveOrgId, _days: dateRange }),
      ]);

      if (journeyRes.error) console.error('Error fetching org prompt journey:', journeyRes.error);
      if (departmentsRes.error) console.error('Error fetching department trends:', departmentsRes.error);
      if (usersRes.error) console.error('Error fetching user rollup:', usersRes.error);
      if (typesRes.error) console.error('Error fetching prompt types:', typesRes.error);
      if (tagsRes.error) console.error('Error fetching tag distribution:', tagsRes.error);
      if (momentumRes.error) console.error('Error fetching org momentum:', momentumRes.error);
      if (workflowsRes.error) console.error('Error fetching workflow metrics:', workflowsRes.error);

      setPromptTypes(typesRes.data || []);
      setTagDistribution(tagsRes.data || []);
      setWorkflowMetrics(workflowsRes.data?.[0] || null);

      const rpcHasErrors = Boolean(journeyRes.error || departmentsRes.error || usersRes.error || momentumRes.error);
      const rpcHasCoreData = Boolean(
        (journeyRes.data || []).length ||
        (departmentsRes.data || []).length ||
        (usersRes.data || []).length ||
        (momentumRes.data || []).length
      );

      if (rpcHasErrors || !rpcHasCoreData) {
        const loadedFallback = await fetchFallbackDashboardData();
        const loadedLegacyFallback = loadedFallback ? true : await fetchLegacyDashboardData();
        if (!loadedLegacyFallback && rpcHasErrors) {
          toast.error('Failed to load dashboard data');
        }
        return;
      }

      setJourneyData((journeyRes.data || []) as OrgJourneyPoint[]);
      setDepartmentTrends((departmentsRes.data || []) as DepartmentTrend[]);
      setUserRollup((usersRes.data || []) as UserPromptRollup[]);
      setOrgMomentum(momentumRes.data?.[0] || null);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      const loadedFallback = await fetchFallbackDashboardData();
      const loadedLegacyFallback = loadedFallback ? true : await fetchLegacyDashboardData();
      if (!loadedLegacyFallback) {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const departments = useMemo(() => (
    ['All', ...departmentTrends.map((dept) => dept.department).filter(Boolean).sort()]
  ), [departmentTrends]);

  const visibleDepartments = departmentFilter === 'All'
    ? departmentTrends
    : departmentTrends.filter((dept) => dept.department === departmentFilter);

  const latestJourneyScore = [...journeyData].reverse().find((point) => point.score !== null)?.score ?? null;
  const activePrompters = visibleDepartments.reduce((sum, dept) => sum + Number(dept.active_users || 0), 0);
  const promptTypeTotal = promptTypes.reduce((sum, item) => sum + Number(item.count || 0), 0);
  const tagTotal = tagDistribution.reduce((sum, item) => sum + Number(item.count || 0), 0);

  const sortedUsers = useMemo(() => {
    const users = [...userRollup];
    return users.sort((a, b) => {
      switch (userSort) {
        case 'score_asc':
          return (a.avg_prompt_score ?? 999) - (b.avg_prompt_score ?? 999);
        case 'prompts_desc':
          return b.prompt_count - a.prompt_count;
        case 'daily_streak_desc':
          return b.daily_streak - a.daily_streak;
        case 'weekly_streak_desc':
          return b.weekly_streak - a.weekly_streak;
        case 'score_desc':
        default:
          return (b.avg_prompt_score ?? -1) - (a.avg_prompt_score ?? -1);
      }
    });
  }, [userRollup, userSort]);

  if (loading) {
    return (
      <MainLayout>
        <main className="pb-16 px-3 md:px-6 pt-6">
          <div className="max-w-7xl mx-auto space-y-6">
            <Skeleton className="h-32 w-full" />
            <div className="grid md:grid-cols-3 gap-6">
              <Skeleton className="h-48" />
              <Skeleton className="h-48" />
              <Skeleton className="h-48" />
            </div>
          </div>
        </main>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <main className="pb-16 px-3 md:px-6 pt-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-display font-bold">Executive Dashboard</h1>
              <p className="text-muted-foreground">{effectiveOrgName}</p>
            </div>
            <div className="flex items-center gap-3">
              <DeltaPeriodSelector value={deltaPeriod} onChange={setDeltaPeriod} />
              <Button variant="outline" onClick={() => navigate('/profile')}>
                <User className="mr-2 h-4 w-4" />
                My Profile
              </Button>
            </div>
          </div>

          <Card className="overflow-hidden border-primary/25 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                AI Building Momentum
              </CardTitle>
              <CardDescription>
                Prompt quality, library growth, and workflow release activity across the selected period.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid gap-3 md:grid-cols-4">
                <div className="rounded-lg border border-primary/20 bg-background/70 p-3">
                  <p className="text-xs font-medium text-muted-foreground">Trending Prompt Score</p>
                  <div className="mt-2"><ScoreDisplay score={latestJourneyScore} /></div>
                  <p className="mt-1 text-xs text-muted-foreground">Latest point</p>
                </div>
                <div className="rounded-lg border border-primary/20 bg-background/70 p-3">
                  <p className="text-xs font-medium text-muted-foreground">Prompt Library Total</p>
                  <p className="mt-2 text-2xl font-bold tabular-nums">{(orgMomentum?.total_prompts || 0).toLocaleString()}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Reusable scored prompts</p>
                </div>
                <div className="rounded-lg border border-primary/20 bg-background/70 p-3">
                  <p className="text-xs font-medium text-muted-foreground">Active Prompters</p>
                  <p className="mt-2 text-2xl font-bold tabular-nums">{activePrompters.toLocaleString()}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Contributing this period</p>
                </div>
                <div className="rounded-lg border border-primary/20 bg-background/70 p-3">
                  <p className="text-xs font-medium text-muted-foreground">Workflows Released</p>
                  <p className="mt-2 text-2xl font-bold tabular-nums">{Number(workflowMetrics?.total_released || 0).toLocaleString()}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{Number(workflowMetrics?.released_in_period || 0).toLocaleString()} in this period</p>
                </div>
              </div>
              {journeyData.length > 0 ? (
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={journeyData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="orgPromptJourneyFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.22} />
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                      <YAxis yAxisId="score" domain={[0, 100]} hide />
                      <YAxis yAxisId="activity" hide />
                      <Tooltip
                        cursor={{ stroke: 'hsl(var(--border))', strokeDasharray: '4 4' }}
                        contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                        formatter={(value, name) => {
                          const numericValue = Number(value);
                          if (name === 'score') return [`${Math.round(numericValue)}/100`, 'Prompt score'];
                          return [`${numericValue} prompt${numericValue === 1 ? '' : 's'}`, 'Activity'];
                        }}
                      />
                      <Bar yAxisId="activity" dataKey="activity" barSize={18} fill="hsl(var(--primary))" opacity={0.38} radius={[4, 4, 0, 0]} />
                      <Area yAxisId="score" type="monotone" dataKey="score" stroke="none" fill="url(#orgPromptJourneyFill)" />
                      <Line yAxisId="score" type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={3} dot={{ r: 3, fill: 'hsl(var(--primary))', strokeWidth: 0 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                  No prompt journey data yet
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.75fr)] gap-6">
            <Card className="bg-card/80 backdrop-blur-sm border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Prompt Category Index
                </CardTitle>
                <CardDescription>Where the organization is applying AI prompting</CardDescription>
              </CardHeader>
              <CardContent className="max-h-[360px] space-y-2 overflow-y-auto">
                {promptTypes.length > 0 ? promptTypes.map((item) => {
                  const count = Number(item.count || 0);
                  const percentage = promptTypeTotal > 0 ? Math.round((count / promptTypeTotal) * 100) : 0;

                  return (
                    <div key={item.prompt_type} className="rounded-lg border border-border/70 bg-muted/20 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <p className="min-w-0 break-words text-sm font-medium leading-snug text-foreground">
                          {item.prompt_type || 'Unknown'}
                        </p>
                        <p className="shrink-0 text-sm tabular-nums text-muted-foreground">
                          {count.toLocaleString()} ({percentage}%)
                        </p>
                      </div>
                    </div>
                  );
                }) : (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    Prompt categories will appear as scored prompts are created.
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="bg-card/80 backdrop-blur-sm border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tags className="h-5 w-5 text-primary" />
                  Demand Themes
                </CardTitle>
                <CardDescription>What people are trying to create, improve, or operationalize</CardDescription>
              </CardHeader>
              <CardContent className="max-h-[360px] space-y-2 overflow-y-auto">
                {tagDistribution.length > 0 ? tagDistribution.map((item) => {
                  const count = Number(item.count || 0);
                  const percentage = tagTotal > 0 ? Math.round((count / tagTotal) * 100) : 0;

                  return (
                    <div key={item.tag} className="rounded-lg border border-border/70 bg-muted/20 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <p className="min-w-0 break-words text-sm font-medium leading-snug text-foreground">
                          {item.tag}
                        </p>
                        <p className="shrink-0 text-sm tabular-nums text-muted-foreground">
                          {count.toLocaleString()} ({percentage}%)
                        </p>
                      </div>
                    </div>
                  );
                }) : (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    Tags will appear as prompts are categorized.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <Card className="bg-card/80 backdrop-blur-sm border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  Department Trends
                </CardTitle>
                <CardDescription>Selected-period activity with total library contribution</CardDescription>
              </CardHeader>
              <CardContent className="max-h-[350px] space-y-2 overflow-y-auto">
                {departmentTrends.length > 0 ? departmentTrends.map((dept) => (
                  <button
                    key={dept.department}
                    className={`w-full rounded-lg border p-3 text-left transition-colors hover:bg-muted/40 ${
                      departmentFilter === dept.department ? 'border-primary bg-primary/10' : 'border-border/70 bg-muted/20'
                    }`}
                    onClick={() => setDepartmentFilter(departmentFilter === dept.department ? 'All' : dept.department)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{dept.department}</p>
                        <p className="text-xs text-muted-foreground">
                          {dept.prompt_count} prompts • {dept.active_users} active
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(dept.total_prompt_count || 0).toLocaleString()} total library prompts
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <ScoreDisplay score={dept.avg_prompt_score} size="sm" />
                          <p className="text-xs text-muted-foreground">{formatDelta(dept.score_delta)}</p>
                        </div>
                      </div>
                    </div>
                  </button>
                )) : (
                  <p className="py-8 text-center text-sm text-muted-foreground">No department trend data yet</p>
                )}
              </CardContent>
            </Card>

            {effectiveOrgId && (
              <TopPromptsRanked
                orgId={effectiveOrgId}
                maxHeight="h-[460px]"
                title="Prompt Library Highlights"
                description="Reusable prompt assets leaders can inspect, share, and build from"
                limit={7}
                showLibraryCta
              />
            )}

            {effectiveOrgId && <ModelSelectionCard orgId={effectiveOrgId} />}
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Card className="bg-card/80 backdrop-blur-sm border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Org Streaks
                </CardTitle>
                <CardDescription>Simple habit metrics that keep momentum visible</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                  <p className="text-xs text-muted-foreground">This Week</p>
                  <p className="mt-2 text-2xl font-bold">{orgMomentum?.prompts_this_week || 0}</p>
                </div>
                <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                  <p className="text-xs text-muted-foreground">Daily Streak</p>
                  <p className="mt-2 text-2xl font-bold">{orgMomentum?.daily_prompt_streak || 0}</p>
                </div>
                <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                  <p className="text-xs text-muted-foreground">Weekly Streak</p>
                  <p className="mt-2 text-2xl font-bold">{orgMomentum?.weekly_prompt_streak || 0}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card/80 backdrop-blur-sm border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Workflow className="h-5 w-5" />
                  Workflow Expansion
                </CardTitle>
                <CardDescription>Reusable operating patterns being built from AI work</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                  <p className="text-xs text-muted-foreground">Created</p>
                  <p className="mt-2 text-2xl font-bold">{Number(workflowMetrics?.total_created || 0).toLocaleString()}</p>
                </div>
                <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                  <p className="text-xs text-muted-foreground">Released</p>
                  <p className="mt-2 text-2xl font-bold">{Number(workflowMetrics?.total_released || 0).toLocaleString()}</p>
                </div>
                <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                  <p className="text-xs text-muted-foreground">Rate</p>
                  <p className="mt-2 text-2xl font-bold">{Number(workflowMetrics?.completion_rate || 0).toLocaleString()}%</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-card/80 backdrop-blur-sm border-border/50">
            <CardHeader>
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Individuals
                  </CardTitle>
                  <CardDescription>
                    Prompt quality, activity, and streaks
                    {departmentFilter !== 'All' && ` • Filtered by ${departmentFilter}`}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                    <SelectTrigger className="w-[180px] bg-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((department) => (
                        <SelectItem key={department} value={department}>{department}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={userSort} onValueChange={(value) => setUserSort(value as UserSort)}>
                    <SelectTrigger className="w-[190px] bg-input">
                      <ArrowUpDown className="mr-2 h-4 w-4" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="score_desc">Top score</SelectItem>
                      <SelectItem value="score_asc">Bottom score</SelectItem>
                      <SelectItem value="prompts_desc">Most scored items</SelectItem>
                      <SelectItem value="daily_streak_desc">Best daily streaks</SelectItem>
                      <SelectItem value="weekly_streak_desc">Best weekly streaks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {sortedUsers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 font-medium">Name</th>
                        <th className="text-left py-3 px-4 font-medium">Department</th>
                        <th className="text-right py-3 px-4 font-medium">Avg Prompt Score</th>
                        <th className="text-right py-3 px-4 font-medium">Prompts</th>
                        <th className="text-right py-3 px-4 font-medium">Daily Streak</th>
                        <th className="text-right py-3 px-4 font-medium">Weekly Streak</th>
                        <th className="text-center py-3 px-4 font-medium">Momentum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedUsers.map((row) => (
                        <tr key={row.user_id} className="border-b border-border/50 hover:bg-muted/30">
                          <td className="py-3 px-4">{row.full_name || 'Unknown'}</td>
                          <td className="py-3 px-4">
                            <Badge variant="outline">{row.department}</Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex justify-end">
                              <ScoreDisplay score={row.avg_prompt_score} size="sm" />
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right">{row.prompt_count}</td>
                          <td className="py-3 px-4 text-right">{row.daily_streak}</td>
                          <td className="py-3 px-4 text-right">{row.weekly_streak}</td>
                          <td className="py-3 px-4">
                            <div className="flex justify-center">{getMomentumIcon(row.momentum)}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  No users found for this filter.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </MainLayout>
  );
}
